export interface FormatNumberShortOptions {
  /** Number of decimal places. Defaults to 1. */
  precision?: number;
  /** Suffix to append (e.g., ' €', '%'). Defaults to empty string. */
  suffix?: string;
  /** Value to return if the input is not a valid number. Defaults to null. */
  invalidInputReturn?: string | null;
  /** Whether to use toLocaleString for numbers < threshold (adds separators). Defaults to false. */
  includeThousandsSeparator?: boolean;
  /** Custom thresholds for K, M, B, T. */
  thresholds?: {
    T?: number;
    B?: number;
    M?: number;
    K?: number;
  };
}

/**
 * Formats a number into a short string representation (e.g., 1.2K, 5M, 1B).
 * Handles K, M, B, T thresholds and allows customization via options.
 *
 * @param value The number or string to format.
 * @param options Configuration options.
 * @returns The formatted string or the value specified in options.invalidInputReturn for invalid inputs.
 */
export function formatNumberShort(
  value: number | string | null | undefined,
  options: FormatNumberShortOptions = {}
): string | null {
  const {
    precision = 1,
    suffix = '',
    invalidInputReturn = null,
    includeThousandsSeparator = false,
    thresholds = {},
  } = options;

  const K = thresholds.K ?? 1e3;
  const M = thresholds.M ?? 1e6;
  const B = thresholds.B ?? 1e9;
  const T = thresholds.T ?? 1e12;

  let num: number;

  if (value === null || value === undefined || value === '') {
    return invalidInputReturn;
  }

  if (typeof value === 'string') {
    num = parseFloat(value);
    if (isNaN(num)) {
      return invalidInputReturn;
    }
  } else {
    num = value;
  }

  if (isNaN(num) || !isFinite(num)) {
      return invalidInputReturn;
  }

  let absNum = Math.abs(num);
  let formattedNum: string;
  let unit = '';

  if (T !== undefined && absNum >= T) {
    unit = 'T';
    formattedNum = (num / T).toFixed(precision);
  } else if (B !== undefined && absNum >= B) {
    unit = 'B';
    formattedNum = (num / B).toFixed(precision);
  } else if (M !== undefined && absNum >= M) {
    unit = 'M';
    formattedNum = (num / M).toFixed(precision);
  } else if (K !== undefined && absNum >= K) {
    unit = 'K';
    formattedNum = (num / K).toFixed(precision);
  } else {
    // Handle numbers smaller than K or if K threshold is not set
    if (precision === 0) {
        formattedNum = Math.round(num).toString(); // Match rounding behavior of layout.tsx < 1000
    } else if (includeThousandsSeparator) {
        formattedNum = num.toLocaleString(undefined, {
            minimumFractionDigits: 0, // Avoid trailing zeros unless necessary
            maximumFractionDigits: precision,
        });
    } else {
        // Basic toString, potentially applying precision if needed, though less common for small numbers
        formattedNum = num.toString();
        // Simple check to potentially match toFixed behavior if precision matters here
         const decimalPointIndex = formattedNum.indexOf('.');
         if (decimalPointIndex !== -1 && formattedNum.length - decimalPointIndex - 1 > precision) {
             // Use toFixed if toString has more decimals than precision allows
             formattedNum = num.toFixed(precision);
         }
    }
  }

  // Remove trailing .0 if precision is > 0 and unit is present
  if (precision > 0 && unit) {
    formattedNum = formattedNum.replace(/\.0$/, '');
  }
  // Remove any potential trailing decimal points if precision caused them (e.g., 123.toFixed(1) -> "123.0")
  if (precision > 0 && !unit && formattedNum.endsWith(`.${'0'.repeat(precision)}`)) {
      formattedNum = formattedNum.substring(0, formattedNum.length - (precision + 1));
  }


  return formattedNum + unit + suffix;
} 